import { useEffect, useState } from "react";
import { Loader2, Wrench, X, Globe2, Copy } from "lucide-react";
import {
    adminToolsOverview,
    adminToolsRecoveryStats,
    adminToolsRecoveryDrilldown,
    api,
} from "@/lib/api";
import { toast } from "sonner";

function MiniStat({ label, value }) {
    return (
        <div className="bl-card p-4">
            <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted">
                {label}
            </div>
            <div className="mt-1.5 font-display font-semibold text-2xl text-bl tnum">
                {value ?? "—"}
            </div>
        </div>
    );
}

function humanizeTool(slug) {
    return slug
        .split("-")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
}

export default function AdminTools() {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [recovery, setRecovery] = useState(null);
    const [costData, setCostData] = useState(null);
    const [drilldown, setDrilldown] = useState(null);
    const [drillLoading, setDrillLoading] = useState(false);

    const openDrilldown = async (toolName) => {
        setDrilldown({ tool_name: toolName, rows: [], count: 0 });
        setDrillLoading(true);
        try {
            const r = await adminToolsRecoveryDrilldown(toolName, 30, 100);
            setDrilldown(r);
        } catch (e) {
            toast.error("Could not load recovery drill-down.");
            setDrilldown(null);
        } finally {
            setDrillLoading(false);
        }
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [r, rec, cost] = await Promise.all([
                    adminToolsOverview(),
                    adminToolsRecoveryStats(30).catch(() => null),
                    api.get("/admin/cost-data/sources").then((r) => r.data).catch(() => null),
                ]);
                if (!cancelled) {
                    setData(r);
                    setRecovery(rec);
                    setCostData(cost);
                }
            } catch (e) {
                toast.error("Could not load tools overview.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    if (loading) {
        return (
            <div
                className="flex items-center gap-2 text-sm text-bl-muted"
                data-testid="admin-tools-loading"
            >
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading…
            </div>
        );
    }

    if (!data) return null;

    // Merge sessions + leads by tool_name for the table view
    const byTool = new Map();
    for (const s of data.by_tool_sessions || []) {
        byTool.set(s.tool_name, {
            tool_name: s.tool_name,
            sessions: s.count,
            leads: 0,
            opt_ins: 0,
        });
    }
    for (const l of data.by_tool_leads || []) {
        const existing = byTool.get(l.tool_name) || {
            tool_name: l.tool_name,
            sessions: 0,
            leads: 0,
            opt_ins: 0,
        };
        existing.leads = l.count;
        existing.opt_ins = l.opt_ins || 0;
        byTool.set(l.tool_name, existing);
    }
    const tableRows = Array.from(byTool.values()).sort(
        (a, b) => b.sessions - a.sessions,
    );

    // Group result levels by tool
    const levelsByTool = new Map();
    for (const r of data.result_levels || []) {
        const k = r.tool_name;
        const m = levelsByTool.get(k) || { low: 0, moderate: 0, high: 0 };
        if (r.level && Object.prototype.hasOwnProperty.call(m, r.level)) {
            m[r.level] += r.count;
        }
        levelsByTool.set(k, m);
    }

    return (
        <div className="space-y-6" data-testid="admin-tools-overview">
            <div className="flex items-center gap-2">
                <Wrench className="h-5 w-5 text-bl-primary" />
                <h2 className="font-display font-semibold text-xl text-bl tracking-tight">
                    SEO Tools — sessions, leads, conversion
                </h2>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <MiniStat
                    label="Total sessions"
                    value={data.total_sessions}
                />
                <MiniStat label="Email captures" value={data.total_leads} />
                <MiniStat
                    label="Marketing opt-ins"
                    value={data.total_opt_ins}
                />
            </div>

            {recovery && (
                <div
                    className="bl-card p-5"
                    data-testid="admin-tools-recovery-stats"
                >
                    <div className="flex items-baseline justify-between gap-4 mb-4">
                        <h3 className="font-display font-semibold text-base text-bl tracking-tight">
                            Save-resume recovery funnel
                        </h3>
                        <span className="text-[10px] tracking-[0.2em] uppercase text-bl-muted">
                            Last {recovery.window_days}d
                        </span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <MiniStat
                            label="Tokens saved"
                            value={recovery.tokens_saved}
                        />
                        <MiniStat
                            label="48h emails sent"
                            value={recovery.followups_sent}
                        />
                        <MiniStat
                            label="Email recoveries"
                            value={
                                (recovery.recoveries_by_source || []).find(
                                    (s) => s.source === "email",
                                )?.count ?? 0
                            }
                        />
                        <MiniStat
                            label="Upload conversions"
                            value={recovery.email_conversions_to_upload}
                        />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                        <MiniStat
                            label="Email → recovery rate"
                            value={
                                recovery.followup_to_recovery_rate != null
                                    ? `${(recovery.followup_to_recovery_rate * 100).toFixed(1)}%`
                                    : "—"
                            }
                        />
                        <MiniStat
                            label="Recovery → upload rate"
                            value={
                                recovery.recovery_to_upload_rate != null
                                    ? `${(recovery.recovery_to_upload_rate * 100).toFixed(1)}%`
                                    : "—"
                            }
                        />
                    </div>
                    <p className="text-xs text-bl-muted mt-4">
                        Tracks save-resume tokens → 48h follow-up email →
                        first-consume recovery → downstream report upload. UTM
                        attribution: <code>utm_source=email</code>,{" "}
                        <code>utm_campaign=tool_resume_48h</code>.
                    </p>

                    {(recovery.recoveries_by_tool || []).length > 0 && (
                        <div
                            className="mt-5 overflow-x-auto"
                            data-testid="admin-tools-recovery-by-tool"
                        >
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left">
                                        <th className="px-3 py-2 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl">
                                            Tool
                                        </th>
                                        <th className="px-3 py-2 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl text-right">
                                            Email
                                        </th>
                                        <th className="px-3 py-2 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl text-right">
                                            Direct
                                        </th>
                                        <th className="px-3 py-2 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl text-right">
                                            Total
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recovery.recoveries_by_tool.map((row) => (
                                        <tr
                                            key={row.tool_name}
                                            className="border-b border-bl/30 last:border-0 cursor-pointer hover:bg-bl-bg/60"
                                            onClick={() => openDrilldown(row.tool_name)}
                                            data-testid={`recovery-row-${row.tool_name}`}
                                        >
                                            <td className="px-3 py-2 text-bl">
                                                {humanizeTool(row.tool_name)}
                                            </td>
                                            <td className="px-3 py-2 text-bl tnum text-right">
                                                {row.email}
                                            </td>
                                            <td className="px-3 py-2 text-bl-muted tnum text-right">
                                                {row.direct}
                                            </td>
                                            <td className="px-3 py-2 text-bl tnum text-right font-semibold">
                                                {row.total}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {(recovery.recoveries_by_variant || []).length > 0 && (
                        <div
                            className="mt-5 overflow-x-auto"
                            data-testid="admin-tools-recovery-by-variant"
                        >
                            <div className="flex items-baseline justify-between gap-4 mb-2">
                                <h4 className="font-display font-medium text-sm text-bl tracking-tight">
                                    Subject-line A/B performance
                                </h4>
                                <span className="text-[10px] tracking-[0.2em] uppercase text-bl-muted">
                                    save_resume_subject_v1
                                </span>
                            </div>
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left">
                                        <th className="px-3 py-2 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl">
                                            Variant
                                        </th>
                                        <th className="px-3 py-2 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl text-right">
                                            Sent
                                        </th>
                                        <th className="px-3 py-2 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl text-right">
                                            Recovered
                                        </th>
                                        <th className="px-3 py-2 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl text-right">
                                            Rate
                                        </th>
                                        <th className="px-3 py-2 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl text-right">
                                            Filter
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recovery.recoveries_by_variant.map((row, i) => (
                                        <tr
                                            key={row.variant_id}
                                            className="border-b border-bl/30 last:border-0"
                                            data-testid={`recovery-variant-row-${row.variant_id}`}
                                        >
                                            <td className="px-3 py-2 text-bl">
                                                <span className="font-mono text-xs">
                                                    {row.variant_id}
                                                </span>
                                                {i === 0 && row.emails_sent >= 30 && (
                                                    <span
                                                        className="ml-2 inline-block text-[9px] tracking-[0.2em] uppercase px-1.5 py-0.5 rounded bg-bl-primary/15 text-bl-primary"
                                                        data-testid={`recovery-variant-leader-${row.variant_id}`}
                                                    >
                                                        Leader
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-bl-muted tnum text-right">
                                                {row.emails_sent}
                                            </td>
                                            <td className="px-3 py-2 text-bl tnum text-right">
                                                {row.email_recoveries}
                                            </td>
                                            <td className="px-3 py-2 text-bl tnum text-right font-semibold">
                                                {row.recovery_rate != null
                                                    ? `${(row.recovery_rate * 100).toFixed(1)}%`
                                                    : "—"}
                                            </td>
                                            <td className="px-3 py-2 text-right">
                                                <button
                                                    type="button"
                                                    title="Copy PostHog filter JSON to clipboard"
                                                    onClick={() => {
                                                        const filter = {
                                                            event: "tool_resume_followup_email_sent",
                                                            properties: [
                                                                {
                                                                    key: "subject_variant",
                                                                    value: row.variant_id,
                                                                    operator: "exact",
                                                                    type: "event",
                                                                },
                                                                {
                                                                    key: "experiment_id",
                                                                    value: "save_resume_subject_v1",
                                                                    operator: "exact",
                                                                    type: "event",
                                                                },
                                                            ],
                                                        };
                                                        try {
                                                            navigator.clipboard.writeText(
                                                                JSON.stringify(filter, null, 2),
                                                            );
                                                            toast.success(
                                                                `Filter for "${row.variant_id}" copied · paste into PostHog → Events`,
                                                            );
                                                        } catch {
                                                            toast.error("Clipboard blocked by browser.");
                                                        }
                                                    }}
                                                    className="bl-btn-ghost text-[11px] gap-1 px-2 py-1"
                                                    data-testid={`recovery-variant-posthog-${row.variant_id}`}
                                                >
                                                    <Copy className="h-3 w-3" />
                                                    PostHog
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <p className="text-[11px] text-bl-muted mt-2">
                                &quot;Leader&quot; tag appears when the top variant has
                                at least 30 emails sent (significance floor).
                            </p>
                        </div>
                    )}
                </div>
            )}

            {costData && (
                <div
                    className="bl-card p-5"
                    data-testid="admin-cost-data-card"
                >
                    <div className="flex items-baseline justify-between gap-4 mb-4">
                        <div className="flex items-center gap-2">
                            <Globe2 className="h-4 w-4 text-bl-primary" />
                            <h3 className="font-display font-semibold text-base text-bl tracking-tight">
                                Localized cost data
                            </h3>
                        </div>
                        <span className="text-[10px] tracking-[0.2em] uppercase text-bl-muted">
                            {costData.engine_version || "cost_engine"}
                        </span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <MiniStat
                            label="ZIP prefixes"
                            value={costData.total_prefixes}
                        />
                        {(costData.by_tier || []).slice(0, 3).map((t) => (
                            <MiniStat
                                key={t.tier}
                                label={`Tier · ${t.tier.replace(/_/g, " ")}`}
                                value={t.count}
                            />
                        ))}
                    </div>
                    {(costData.sources || []).length > 0 && (
                        <div className="mt-5">
                            <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium mb-2">
                                Source attribution
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {costData.sources.map((s) => (
                                    <span
                                        key={s.source}
                                        className="text-xs px-2.5 py-1 rounded-md bg-[var(--bl-stone-soft)] text-bl font-mono"
                                        data-testid={`cost-source-${s.source}`}
                                    >
                                        {s.source} · {s.entries}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                    <p className="text-xs text-bl-muted mt-4">
                        Merged at startup with hardcoded MARKET_PROFILES. CSV
                        wins on conflict so you can override without code
                        changes. Source CSV: <code>{costData.csv_path}</code>.
                    </p>
                </div>
            )}

            <div className="bl-card overflow-x-auto" data-testid="admin-tools-table">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left">
                            <th className="px-5 py-3 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl">
                                Tool
                            </th>
                            <th className="px-5 py-3 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl">
                                Sessions
                            </th>
                            <th className="px-5 py-3 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl">
                                Leads
                            </th>
                            <th className="px-5 py-3 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl">
                                Opt-ins
                            </th>
                            <th className="px-5 py-3 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl">
                                Low / Mod / High
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {tableRows.length === 0 && (
                            <tr>
                                <td
                                    colSpan={5}
                                    className="px-5 py-8 text-center text-bl-muted"
                                >
                                    No data yet
                                </td>
                            </tr>
                        )}
                        {tableRows.map((r) => {
                            const lvl = levelsByTool.get(r.tool_name) || {
                                low: 0,
                                moderate: 0,
                                high: 0,
                            };
                            return (
                                <tr
                                    key={r.tool_name}
                                    className="border-b border-bl/40 last:border-b-0"
                                    data-testid={`admin-tools-row-${r.tool_name}`}
                                >
                                    <td className="px-5 py-3 text-bl">
                                        {humanizeTool(r.tool_name)}
                                    </td>
                                    <td className="px-5 py-3 font-mono">
                                        {r.sessions}
                                    </td>
                                    <td className="px-5 py-3 font-mono">
                                        {r.leads}
                                    </td>
                                    <td className="px-5 py-3 font-mono">
                                        {r.opt_ins}
                                    </td>
                                    <td className="px-5 py-3 font-mono text-xs">
                                        {lvl.low} / {lvl.moderate} / {lvl.high}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {data.top_zips?.length > 0 && (
                <div>
                    <h3 className="font-display font-semibold text-base text-bl mb-2">
                        Top ZIPs (where available)
                    </h3>
                    <div className="flex flex-wrap gap-2">
                        {data.top_zips.map((z) => (
                            <span
                                key={z.zip_code}
                                className="text-xs px-2.5 py-1 rounded-md bg-[var(--bl-stone-soft)] text-bl font-mono"
                            >
                                {z.zip_code} · {z.count}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {drilldown && (
                <div
                    className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-6"
                    onClick={() => setDrilldown(null)}
                    data-testid="recovery-drilldown-overlay"
                >
                    <div
                        className="bl-card w-full sm:max-w-3xl max-h-[85vh] overflow-y-auto bg-white"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="recovery-drilldown-modal"
                    >
                        <div className="flex items-center justify-between p-5 border-b border-bl">
                            <div>
                                <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted">
                                    Recovery drill-down · last {drilldown.window_days || 30}d
                                </div>
                                <h3 className="font-display font-semibold text-base text-bl mt-1">
                                    {humanizeTool(drilldown.tool_name)} ·{" "}
                                    {drilldown.count} sessions
                                </h3>
                            </div>
                            <button
                                onClick={() => setDrilldown(null)}
                                className="p-1.5 rounded-md hover:bg-bl-bg/60"
                                data-testid="recovery-drilldown-close"
                                aria-label="Close"
                            >
                                <X className="h-4 w-4 text-bl-muted" />
                            </button>
                        </div>
                        {drillLoading ? (
                            <div className="flex items-center gap-2 text-sm text-bl-muted p-6">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Loading…
                            </div>
                        ) : drilldown.rows.length === 0 ? (
                            <div className="p-6 text-sm text-bl-muted">
                                No saved sessions for this tool in the
                                window.
                            </div>
                        ) : (
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left">
                                        <th className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl">
                                            Email
                                        </th>
                                        <th className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl">
                                            Status
                                        </th>
                                        <th className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl">
                                            Variant
                                        </th>
                                        <th className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl">
                                            ZIP
                                        </th>
                                        <th className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl">
                                            Saved
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {drilldown.rows.map((row) => (
                                        <tr
                                            key={row.resume_token}
                                            className="border-b border-bl/30 last:border-0"
                                            data-testid={`drill-row-${row.resume_token}`}
                                        >
                                            <td className="px-4 py-2 text-bl">
                                                {row.email || "—"}
                                            </td>
                                            <td className="px-4 py-2 text-bl">
                                                <span
                                                    className={
                                                        row.status ===
                                                        "converted_to_upload"
                                                            ? "text-bl-primary font-semibold"
                                                            : row.status ===
                                                                  "recovered"
                                                                ? "text-bl"
                                                                : "text-bl-muted"
                                                    }
                                                >
                                                    {row.status.replace(
                                                        /_/g,
                                                        " ",
                                                    )}
                                                </span>
                                            </td>
                                            <td className="px-4 py-2 text-xs text-bl-muted">
                                                {row.followup_subject_variant ||
                                                    "—"}
                                            </td>
                                            <td className="px-4 py-2 text-xs text-bl-muted font-mono">
                                                {row.zip_code || "—"}
                                            </td>
                                            <td className="px-4 py-2 text-xs text-bl-muted">
                                                {row.created_at
                                                    ? new Date(
                                                          row.created_at,
                                                      ).toLocaleDateString()
                                                    : "—"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
